import ServiceList, {
  useCategories
} from "../../elements/ServiceList/ServiceList";
import ServiceBox from "../../elements/ServiceBox/ServiceBox";
import "./Service.css";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState, useContext } from "react";
import { formatCategoryName, noSlash } from "../../App";
import { faqs, getTags } from "../../meta";
import { Helmet } from "react-helmet";
import { CategoryContext, LocaleContext } from "../../AppProvider";
import { useAsync } from "../../utilities/asyncFunc";
import { getAllServices } from "../../state_management/epics";
import { service } from "../../state_management/actionTypes";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevron-down.svg";
import { queryServices } from "../../state_management/epics/services";

const Services = () => {
  const { t } = useContext(LocaleContext);
  const [categories] = useContext(CategoryContext);
  const [unavailable, setUnavailable] = useState(false);

  const { serviceName } = useParams();
  const [searchParams] = useSearchParams();
  const query = searchParams.get("query") || "";

  const [results, setResults] = useState<service[]>([]);

  // If query exists, pull from context or API
  const res = useAsync(
    {
      asyncFunc: queryServices,
      funcParams: { query },
      immediate: true,
      canCall: Boolean(query)
    },
    [query]
  );

  useEffect(() => {
    if (query && res.data) {
      setResults(res.data);
    } else if (!query) {
      setResults([]);
    }
  }, [query, res.data]);

  const categoryObject = useMemo(() => {
    const catObj = categories.find(
      (category) => noSlash(formatCategoryName(category)) === serviceName
    );

    return catObj;
  }, [JSON.stringify(categories), serviceName]);

  const categoryIDs = useCategories().categories;

  const serviceCategory = serviceName
    ? [...(categoryObject ? [categoryObject.id] : [])]
    : categoryIDs;

  useEffect(() => {
    if (serviceName && categories.length) {
      if (categoryObject) {
        setUnavailable(false);
      } else {
        setUnavailable(true);
      }
    } else if (!serviceName) {
      setUnavailable(false);
    }
  }, [serviceName, categories]);

  const Name = (() => {
    if (query) return `Results for "${query}"`;
    if (serviceCategory.length === 1) {
      const cat = categories.find(
        (category) => category.id === serviceCategory[0]
      );
      return cat?.name || t("All Services");
    } else {
      return t("All Services");
    }
  })();

  const tag = getTags(serviceName);

  return (
    <div className="service_wrapper">
      {tag ? (
        <Helmet>
          <title>{tag.header}</title>
          <meta name="description" content={tag.description} />
        </Helmet>
      ) : (
        <></>
      )}
      {!unavailable ? (
        categories.length || query ? (
          <>
            <p>{Name}</p>
            <div className="service_body flex">
              {serviceName ? <></> : <ServiceList categories={categories} />}
              <div className="service_body_item_wrapper">
                <ServiceBox
                  Name={Name}
                  categoryObject={categoryObject}
                  categoryIDs={serviceCategory}
                  listHidden={!!serviceName}
                  loading={query ? res.loading : undefined}
                  defaultServices={query ? results : undefined}
                />
                {Boolean(serviceName) && <ServiceFaq />}
              </div>
            </div>
          </>
        ) : (
          <div />
        )
      ) : (
        <div className="services_notFound">
          <h1 className="bold_text">{t("Page not found")}</h1>
          <p className="med_text">
            {t(
              "The link you followed may be broken, or the page may have been removed."
            )}
          </p>
        </div>
      )}
    </div>
  );
};

const ConnectedService = Services;

export default ConnectedService;

export const CleaningProducts = () => {
  const [services, setServices] = useState<service[]>([]);

  const res = useAsync(
    {
      asyncFunc: getAllServices,
      funcParams: {
        type: "custom_url",
        show_in: "cleaning_product",
        categories: []
      },
      immediate: true
    },
    []
  );

  useEffect(() => {
    if (res.data) {
      setServices([...res.data]);
    }
  }, [res.data]);

  return (
    <div className="service_wrapper">
      <Helmet>
        <title>Cleaning products | CleanBee.ie</title>
        <meta
          name="description"
          content={
            "Cleaning products ✴️ Book here the cleaning of your oven, kitchen or entire house ❗❱❱❱ Visit and learn more!"
          }
        />
      </Helmet>
      <p>Cleaning Products</p>
      <div className="service_body flex">
        {res.loading ? (
          <p style={{ textAlign: "center", width: "100%" }}>Loading...</p>
        ) : (
          <ServiceBox
            Name={"Cleaning Products"}
            defaultServices={services}
            categoryIDs={[]}
            listHidden
          />
        )}
      </div>
    </div>
  );
};

const ServiceFaq = () => {
  const [open, setOpen] = useState<number>();

  const location = useLocation();
  const { pathname } = location;
  const questionObj = faqs.find((faq) =>
    pathname.toLowerCase().startsWith(faq.link.toLowerCase())
  );

  if (!questionObj) return null;
  return (
    <div className="service_box_faq">
      <div className="service_box_faq_title">FAQ</div>
      <div className="service_box_faq_list">
        {questionObj.questions.map((question, index) => (
          <div className="service_box_faq_list_item" key={index}>
            <div
              onClick={() => {
                if (open === index) return setOpen(undefined);
                setOpen(index);
              }}
            >
              <div>{question.question}</div>
              <ChevronDown
                className={`service_box_faq_list_item_icon ${
                  open === index ? "open" : ""
                }`}
              />
            </div>
            <div
              className={`service_box_faq_list_desc ${
                open === index ? "active" : ""
              }`}
            >
              {question.answer}
            </div>
          </div>
        ))}
      </div>
      <div className="service_box_faq_contact">
        Have any other questions? Use our AI chat in the bottom corner or call
        us at{" "}
        <a href="tel:+35316994255">
          <span>+353 1699 4255</span>
        </a>
      </div>
    </div>
  );
};
